import {Injectable} from '@angular/core';
import {ComponentType} from '@angular/cdk/portal';
import {Observable, BehaviorSubject, Subject} from 'rxjs';

export interface WebmateToolComponent<T> extends ComponentType<T> {
}

export interface WebmateToolConfig {
    toolName: string;
    scrollable?: boolean;
    // Set true when the tool provides its own header
    hideHeader?: boolean;
    // Set true to hide the close button and prevent the tool from closing by clicking the background
    notDismissible?: boolean;
    // Set true to override the 80% fixed height of the container
    noFixedHeight?: boolean;
    // Set true when a tool needs up to 90% of the viewport width
    wideContainer?: boolean;
}


/**
 * Manages modal tools shown in webmate.
 */
@Injectable({
    'providedIn': 'root'
})
export class ModalToolContainerService {

    private toolChainSubject = new BehaviorSubject<Array<[WebmateToolComponent<any>, WebmateToolConfig]>>([]);
    private lifecycleSubject = new Subject<ModalToolLifecycleEvent>();

    // tslint:disable-next-line:variable-name
    private _toolChain: Array<[any /* WebmateToolComponent<T> */, WebmateToolConfig]> = [];

    private toolContainerDisplayed$ = new BehaviorSubject<boolean>(false);

    private openConsentModalSubject = new Subject<void>();
    openConsentModal$ = this.openConsentModalSubject.asObservable();

    constructor() {
    }

    toolChain$(): Observable<Array<[WebmateToolComponent<any>, WebmateToolConfig]>> {
        return this.toolChainSubject.asObservable();
    }

    getEvents$(): Observable<ModalToolLifecycleEvent> {
        return this.lifecycleSubject.asObservable();
    }

    toolContainerDisplayed(): Observable<boolean> {
        return this.toolContainerDisplayed$.asObservable();
    }

    /**
     * Creates a new tool chain. If there is already another tool chain active, the operation is a noop.
     */
    openTool<T>(toolComponent: ComponentType<T>, toolConfig: WebmateToolConfig) {
        this.showToolContainer();
        this.toolChain = [...this._toolChain, [toolComponent, toolConfig]];
        this.emitLifecycle({
            type: 'open',
            toolComponent,
            toolConfig,
            chainLength: this._toolChain.length
        });
    }


    /**
     * shows the tracking consent modal window
     */
    openConsentModal(): void {
        this.openConsentModalSubject.next();
    }


    /**
     * Closes the currently open tool and replaces it with the specified one
     */
    replaceCurrentTool<T>(toolComponent: ComponentType<T>, toolConfig: WebmateToolConfig) {
        if (this._toolChain.length === 0) {
            return;
        }
        const next = this._toolChain.slice();
        next[next.length - 1] = [toolComponent, toolConfig];
        this.toolChain = next;
        this.emitLifecycle({
            type: 'replace',
            toolComponent,
            toolConfig,
            chainLength: this._toolChain.length
        });
    }

    /**
     * Closes the topmost tool in the chain
     */
    closeLastTool() {
        const next = this._toolChain.slice(0, -1);
        this.toolChain = next;
        this.emitLifecycle({
            type: 'close',
            chainLength: this._toolChain.length
        });
        if (this._toolChain.length <= 0) {
            this.disposeToolContainer();
        }
    }

    set toolChain(list: Array<[WebmateToolComponent<any>, WebmateToolConfig]>) {
        this._toolChain = list;
        this.toolChainSubject.next(this._toolChain);
    }

    showToolContainer() {
        this.toolContainerDisplayed$.next(true);
        this.emitLifecycle({type: 'show', chainLength: this._toolChain.length});
    }

    disposeToolContainer() {
        this.toolChain = [];
        this.toolContainerDisplayed$.next(false);
        this.emitLifecycle({type: 'dispose', chainLength: this._toolChain.length});
    }

    private emitLifecycle(event: ModalToolLifecycleEvent) {
        this.lifecycleSubject.next(event);
    }

}

export type ModalToolLifecycleEvent = {
    type: 'show' | 'open' | 'replace' | 'close' | 'dispose';
    chainLength: number;
    toolComponent?: ComponentType<any>;
    toolConfig?: WebmateToolConfig;
};
